import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiEye } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from '../../components/common/SearchableSelect';
import { formatDate } from '../../utils/format';

export default function Gatepasses() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', type: '', per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.gatepasses.list(params);
      const raw = Array.isArray(data.data) ? data.data : data.gatepasses || data;
      const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.data) ? raw.data : [];
      setRows(list);
      setMeta(raw && !Array.isArray(raw)
        ? { current_page: raw.current_page, last_page: raw.last_page, total: raw.total, per_page: raw.per_page, per_page_options: data.per_page_options || raw.per_page_options }
        : null);
    } catch (err) {
      showError('Failed to load gatepasses', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`gatepass ${row.gatepass_no || '#' + row.id}`);
    if (!res.isConfirmed) return;
    try {
      await api.gatepasses.remove(row.id);
      toast('success', '', 'Gatepass deleted');
      load();
    } catch (err) {
      showError('Failed to delete gatepass', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Gatepass"
        subtitle="Material in/out records."
        crumbs={[{ label: 'Inventory' }, { label: 'Gatepass' }]}
        actions={hasPermission('create-gatepasses') && (
          <button className="btn btn-primary" onClick={() => navigate('/gatepasses/new')}><FiPlus /> New Gatepass</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search gatepass no/party..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect options={[{ value: '', label: 'All Types' }, { value: 'IN', label: 'IN' }, { value: 'OUT', label: 'OUT' }]} value={params.type} onChange={(v) => setParams({ ...params, type: v, page: 1 })} placeholder="All Types" />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="gatepasses" label="gatepasses" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No gatepasses found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'gatepass_no', label: 'Gatepass No', render: (r) => <b>{r.gatepass_no}</b> },
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'type', label: 'Type', render: (r) => <StatusBadge status={r.type} /> },
              { key: 'company_name', label: 'Company', render: (r) => r.party?.name || r.other_party_name || '—' },
              { key: 'person_name', label: 'Person' },
              { key: 'vehicle_no', label: 'Vehicle' },
              { key: 'created_by', label: 'Created By', render: (r) => r.creator?.name || '—' },
              { key: 'articles', label: 'Articles', render: (r) => {
                const arts = r.articles || r.items || [];
                return arts.length ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {arts.map((a, i) => <span key={i} className="badge badge-green">{a.article_no} × {a.quantity}</span>)}
                  </div>
                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>;
              } },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('view-gatepasses') && <button className="btn-icon" onClick={() => navigate(`/gatepasses/${r.id}`)} title="View"><FiEye /></button>}
                  {hasPermission('edit-gatepasses') && <button className="btn-icon" onClick={() => navigate(`/gatepasses/${r.id}/edit`)}><FiEdit2 /></button>}
                  {hasPermission('delete-gatepasses') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}